import {
	Button,
	CalendarHighlightDateOnlyRange,
	Checkbox,
	CheckboxColor,
	CheckboxProps,
	TreeAccordion
} from "client/Components";
import {
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { CalendarColor, CalendarKind } from "client/Api/Calendar/calendar";
import { SidebarCalendar } from "./SidebarCalendar";
import { partition } from "client_legacy/ndaw/arrayHelpers";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import { useToggleExpansion } from "client/Components/TreeAccordion/useToggleExpansion";
import { TimeZone } from "client/DateAndTime/timezones";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { CalendarItem } from "../common";
import styles from "./CalendarSidebarContent.module.less";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { LoadingStatus } from "../useAppointmentLoading";

export type CalendarSidebarContentProps = {
	date: DateOnly | undefined;
	calculateHighlightRangeForDate(date: DateOnly): CalendarHighlightDateOnlyRange;
	calendars: readonly CalendarItem[];
	onDateChange(date: DateOnly): void;
	onSidebarNavigation(): void;
	onIsEnabledChange(
		id: string,
		isEnabled: boolean,
		signal: AbortSignal
	): void;
	timezone: TimeZone;
	loadingStatusMap: ReadonlyMap<string, LoadingStatus>;
	onAddCalendar: () => void;
	onDeleteContactCalendar: (id: string) => void;
};

export const CalendarSidebarContent = (props: CalendarSidebarContentProps): ReactElement => {
	const {
		date,
		calculateHighlightRangeForDate,
		calendars,
		onDateChange,
		onSidebarNavigation,
		onIsEnabledChange,
		timezone,
		loadingStatusMap,
		onAddCalendar,
		onDeleteContactCalendar
	} = props;

	const { isExpanded, toggleExpansion } = useToggleExpansion([MY_CALENDARS_ID, OTHER_CALENDARS_ID]);

	const changeHandler = useCallback(
		(value: DateOnly): void => {
			onDateChange(value);
			onSidebarNavigation();
		},
		[
			onDateChange,
			onSidebarNavigation
		]
	);

	const [
		personalCalendars,
		otherCalendars
	] = useMemo(
		(): [
			personalCalendars: readonly CalendarItem[],
			otherCalendars: readonly CalendarItem[]
		] => {
			return partition(
				calendars,
				(calendar) => calendar.kind === CalendarKind.Personal
			);
		},
		[calendars]
	);

	return (
		<>
			<SidebarCalendar
				date={date}
				onChange={changeHandler}
				timezone={timezone}
				calculateHighlightRangeForDate={calculateHighlightRangeForDate}
			/>
			<div className={styles.addButtonContainer}>
				<Button
					kind="transparent"
					icon="plus"
					text={localize("_AddCalendar")}
					onClick={onAddCalendar}
					size="large"
					tooltip={localize("_AddCalendar")}
					trackingId="add-calendar"
				/>
			</div>
			<TreeAccordion trackingId="calendars">
				{personalCalendars.length > 0 && (
					<TreeAccordion.Node>
						<TreeAccordion.Item
							trackingId="my-calendars__header"
							expansionToggle="visible"
							isExpanded={isExpanded(MY_CALENDARS_ID)}
							onToggleExpansion={() => toggleExpansion(MY_CALENDARS_ID)}
							onClick={() => toggleExpansion(MY_CALENDARS_ID)}
						>
							{localize("_MyCalendars")}
						</TreeAccordion.Item>
						<TreeAccordion.SubTree
							isCollapsed={!isExpanded(MY_CALENDARS_ID)}
							trackingId="my-calendars"
						>
							{personalCalendars.map((x) => {
								const status: LoadingStatus | undefined = loadingStatusMap.get(x.id);

								return (
									<TreeAccordion.Node key={x.id}>
										<TreeAccordion.Item
											trackingId={generateTrackingId("item", `|${x.id}`)}
											indentionLevel={1}
											stretchContentHeight
										>
											<CalendarCheckbox
												trackingId="checkbox"
												onIsEnabledChange={onIsEnabledChange}
												id={x.id}
												value={x.isEnabled}
												text={x.name}
												title={x.name}
												color={x.isEnabled
													? calendarColorToCheckboxColorMap[x.color]
													: undefined
												}
												status={status}
											/>
										</TreeAccordion.Item>
									</TreeAccordion.Node>
								);
							})}
						</TreeAccordion.SubTree>
					</TreeAccordion.Node>
				)}
				{otherCalendars.length > 0 && (
					<TreeAccordion.Node>
						<TreeAccordion.Item
							trackingId="other-calendars__header"
							expansionToggle="visible"
							isExpanded={isExpanded(OTHER_CALENDARS_ID)}
							onToggleExpansion={() => toggleExpansion(OTHER_CALENDARS_ID)}
							onClick={() => toggleExpansion(OTHER_CALENDARS_ID)}
						>
							{localize("_OtherCalendars")}
						</TreeAccordion.Item>
						<TreeAccordion.SubTree
							isCollapsed={!isExpanded(OTHER_CALENDARS_ID)}
							trackingId="other-calendars"
						>
							{otherCalendars.map((x) => {
								const status: LoadingStatus | undefined = loadingStatusMap.get(x.id);

								return (
									<TreeAccordion.Node key={x.id}>
										<TreeAccordion.Item
											trackingId={generateTrackingId("item", `|${x.id}`)}
											indentionLevel={1}
											stretchContentHeight
											action={x.kind === CalendarKind.Contact
												? (
													<ContactCalendarItemActions
														name={x.name}
														onDelete={() => onDeleteContactCalendar(x.id)}
													/>
												)
												: undefined
											}
										>
											<CalendarCheckbox
												trackingId="checkbox"
												onIsEnabledChange={onIsEnabledChange}
												id={x.id}
												value={x.isEnabled}
												text={x.name}
												title={x.name}
												color={
													isNotNil(x.configuration)
														? calendarColorToCheckboxColorMap[x.configuration.color]
														: undefined
												}
												status={status}
											/>
										</TreeAccordion.Item>
									</TreeAccordion.Node>
								);
							})}
						</TreeAccordion.SubTree>
					</TreeAccordion.Node>
				)}
			</TreeAccordion>
		</>
	);
};
CalendarSidebarContent.displayName = "CalendarSidebarContent";

type ContactCalendarItemActionsProps = {
	name: string;
	onDelete: () => void;
};

const ContactCalendarItemActions = (props: ContactCalendarItemActionsProps): ReactElement => {
	const {
		name,
		onDelete
	} = props;

	const actions = useMemo(
		(): readonly Action<{}>[] => getItemActions(
			onDelete
		),
		[onDelete]
	);

	return (
		<TreeAccordion.ActionList
			actions={actions}
			context={{}}
			menuTitle={name}
		/>
	);
};
ContactCalendarItemActions.DisplayName = "ContactCalendarItemActions";

function getItemActions(
	onRemove: () => void
): readonly Action<{}>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "remove",
			title: localize("_Remove"),
			icon: "trash-can",
			canExecute: () => true,
			execute: () => onRemove()
		}
	];
}

type CalendarCheckboxProps = Omit<CheckboxProps, "onChange"> & {
	onIsEnabledChange(
		id: string,
		isEnabled: boolean,
		signal: AbortSignal
	): void;
	id: string;
	status?: LoadingStatus;
};

const CalendarCheckbox = (props: CalendarCheckboxProps): ReactElement => {
	const {
		onIsEnabledChange,
		id,
		status,
		...rest
	} = props;

	const calendarToggleCancellation: RefObject<AbortController | undefined> = useRef<AbortController>(undefined);

	const changeHandler = useCallback(
		(isEnabled: boolean): void => {
			calendarToggleCancellation.current
				?.abort();

			const cancellation = new AbortController();
			calendarToggleCancellation.current = cancellation;

			onIsEnabledChange(
				id,
				isEnabled,
				cancellation.signal
			);
		},
		[id, onIsEnabledChange]
	);

	return (
		<Checkbox
			{...rest}
			onChange={changeHandler}
			hasBusyIndicator={status === "loading"}
			icon={status === "error" || status === "access-denied"
				? {
					color: "danger",
					kind: "font",
					name: "triangle-exclamation"
				}
				: undefined
			}
			ellipseText
			isCentered
			title={status === "access-denied" || status === "error"
				? localize(statusToErrorMessageMap[status])
				: undefined
			}
		/>
	);
};
CalendarCheckbox.displayName = "CalendarCheckbox";

const MY_CALENDARS_ID = "my-calendars";
const OTHER_CALENDARS_ID = "other-calendars";

const calendarColorToCheckboxColorMap: { [key in CalendarColor]: CheckboxColor } = {
	[CalendarColor.Warning]: "warning",
	[CalendarColor.Success]: "success",
	[CalendarColor.Info]: "info",
	[CalendarColor.Error]: "danger",
	[CalendarColor.Color1]: "calendar-1",
	[CalendarColor.Color2]: "calendar-2",
	[CalendarColor.Color3]: "calendar-3",
	[CalendarColor.Color4]: "calendar-4",
	[CalendarColor.Color5]: "calendar-5",
	[CalendarColor.Color6]: "calendar-6",
	[CalendarColor.Color7]: "calendar-7",
	[CalendarColor.Color8]: "calendar-8",
	[CalendarColor.Color9]: "calendar-9",
	[CalendarColor.Color10]: "calendar-10",
	[CalendarColor.Color11]: "calendar-11",
	[CalendarColor.Color12]: "calendar-12",
	[CalendarColor.Color13]: "calendar-13",
	[CalendarColor.Color14]: "calendar-14",
	[CalendarColor.Color15]: "calendar-15",
	[CalendarColor.Color16]: "calendar-16"
};

const statusToErrorMessageMap: Record<Exclude<LoadingStatus, "loading" | "success">, LocalizationKey> = {
	"access-denied": "_NoPermissionsToReadCalendar",
	"error": "_ErrorDuringAppointmentRetrieval"
};
